import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { UsersThree, CalendarBlank, MapPin, CaretRight } from "@phosphor-icons/react";

const fmtDate = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }) : "Unscheduled");

export default function TeamScheduleCard() {
  const [users, setUsers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [selected, setSelected] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.listUsers().then(setUsers).catch(() => {});
    api.listJobs({}).then(setJobs).catch(() => {});
  }, []);

  const countFor = (uid) => jobs.filter((j) => (j.assigned_crew_ids || []).includes(uid) && j.status !== "completed" && j.status !== "cancelled").length;

  const assigned = useMemo(() => {
    if (!selected) return [];
    return jobs
      .filter((j) => (j.assigned_crew_ids || []).includes(selected))
      .sort((a, b) => {
        const ad = a.scheduled_date || "9999", bd = b.scheduled_date || "9999";
        if (ad !== bd) return ad < bd ? -1 : 1;
        return (a.scheduled_time || "") < (b.scheduled_time || "") ? -1 : 1;
      });
  }, [selected, jobs]);

  const selectedUser = users.find((u) => u.id === selected);

  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-5 mb-6 fade-up" data-testid="team-schedule-card">
      <div className="flex items-center gap-2 mb-4">
        <UsersThree size={18} weight="duotone" className="text-[#00B4D8]" />
        <h3 className="font-display font-semibold text-slate-900">Team &amp; Assignments</h3>
        <span className="text-xs text-slate-400">Click an employee to see their scheduled work</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {/* Employee list */}
        <div className="space-y-1.5 md:border-r md:border-slate-100 md:pr-4">
          {users.length === 0 && <p className="text-sm text-slate-400">No team members yet.</p>}
          {users.map((u) => {
            const n = countFor(u.id);
            const active = selected === u.id;
            return (
              <button
                key={u.id} onClick={() => setSelected(u.id)}
                data-testid={`team-employee-${u.id}`}
                className={`w-full flex items-center gap-3 rounded-lg p-2.5 text-left transition-colors duration-200 ${active ? "bg-cyan-50 ring-1 ring-[#00B4D8]" : "hover:bg-slate-50"}`}
              >
                <div className="w-9 h-9 rounded-full bg-[#0B1726] text-white flex items-center justify-center text-sm font-bold overflow-hidden shrink-0">
                  {u.avatar_url ? <img src={u.avatar_url} alt="" className="w-full h-full object-cover" /> : (u.name || "?")[0]?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-800 text-sm truncate">{u.name}</div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-400 capitalize">{u.role}</div>
                </div>
                {n > 0 && <span className="shrink-0 bg-[#00B4D8] text-white text-[11px] font-bold px-2 py-0.5 rounded-full">{n}</span>}
                <CaretRight size={14} className={`shrink-0 transition-transform duration-200 ${active ? "text-[#00B4D8] translate-x-0.5" : "text-slate-300"}`} />
              </button>
            );
          })}
        </div>

        {/* Assigned schedule */}
        <div className="md:col-span-2">
          {!selected ? (
            <div className="h-full flex items-center justify-center text-center text-slate-400 text-sm py-10 border border-dashed border-slate-200 rounded-lg">
              Select an employee to view their assigned jobs.
            </div>
          ) : (
            <>
              <div className="text-sm font-semibold text-slate-700 mb-3">{selectedUser?.name}'s schedule <span className="text-slate-400 font-normal">· {assigned.length} job{assigned.length === 1 ? "" : "s"}</span></div>
              {assigned.length === 0 ? (
                <p className="text-sm text-slate-400">No jobs assigned to {selectedUser?.name} yet.</p>
              ) : (
                <div className="space-y-2" data-testid="team-assigned-list">
                  {assigned.map((j) => (
                    <button
                      key={j.id} onClick={() => navigate(`/jobs/${j.id}`)}
                      data-testid={`team-assigned-job-${j.id}`}
                      className="w-full text-left bg-white border border-slate-200 rounded-lg p-3 flex items-center gap-3 hover:border-[#00B4D8] hover:shadow-sm transition-all duration-200"
                    >
                      <div className="w-10 shrink-0 text-center">
                        <div className="text-[10px] uppercase text-slate-400 leading-none">{j.scheduled_date ? fmtDate(j.scheduled_date).split(",")[0] : "—"}</div>
                        <div className="text-lg font-bold text-slate-800 leading-tight">{j.scheduled_date ? new Date(j.scheduled_date + "T00:00:00").getDate() : "?"}</div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-[#00B4D8] text-xs font-bold">{j.number}</span>
                          <StatusBadge status={j.status} />
                        </div>
                        <div className="font-semibold text-slate-800 text-sm truncate">{j.title}</div>
                        <div className="text-xs text-slate-500 flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
                          <span className="flex items-center gap-1"><CalendarBlank size={12} weight="duotone" />{fmtDate(j.scheduled_date)}{j.scheduled_time ? ` · ${j.scheduled_time}` : ""}</span>
                          {j.client_name && <span className="flex items-center gap-1"><MapPin size={12} weight="duotone" />{j.client_name}</span>}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
